<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useField, useFormContext } from 'vee-validate'
import { computed, onMounted, ref, watch } from 'vue'

import type { TWOrganizationAIResponse } from '@/types/external/2work/doctypes/generatorOutput/ERP2Work/TWOrganizationAIResponse'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import CollapsableContent from '@/components/common/layout/CollapsableContent.vue'
import InputGrid from '@/components/common/ui/inputs/InputGrid.vue'
import SingleSelect from '@/components/common/ui/inputs/SingleSelect.vue'
import TextInput from '@/components/common/ui/inputs/TextInput.vue'
import TWTextArea from '@/components/common/ui/inputs/TWTextArea.vue'
import { RelationType } from '@/constants/relations'
import { useBackendConstantsStore } from '@/stores/backendConstantsStore'
import { translationFunctionDefault } from '@/utilities/translations'

interface SectionGeneralDataOrganizationProps {
  aiData?: null | TWOrganizationAIResponse | undefined
  aiLoading?: boolean | undefined
  inCreateContext?: boolean
  isPersonCustomer?: boolean
  onSubmit?: (() => void) | undefined
  orgDoctype?: RelationType | string | undefined
  showAiButton?: boolean | undefined
}

const props = defineProps<SectionGeneralDataOrganizationProps>()

const emit = defineEmits<{
  aiCancel: []
  aiClick: [organizationName: string]
  nameChange: []
}>()

const handleOrganizationNameBlur = (): void => {
  props.onSubmit?.()
  emit('nameChange')
}

const handleOrganizationNameInput = (): void => {
  emit('nameChange')
}

const tfDefault = translationFunctionDefault()
const { setFieldValue, values: formValues } = useFormContext()

const backendConstantsStore = useBackendConstantsStore()
const { fetchCustomerGroupList, fetchIndustryList, fetchSupplierGroupList } = backendConstantsStore
const { customerGroupOptionList, industryOptionList, supplierGroupOptionList } =
  storeToRefs(backendConstantsStore)

const currentDoctype = computed(() => props.orgDoctype ?? formValues['doctype'])

const isCustomer = computed(() => currentDoctype.value === RelationType.customers)
const isSupplier = computed(() => currentDoctype.value === RelationType.suppliers)

const aiFilledFields = ref<Set<string>>(new Set())

const getAiFieldCount = (): number => {
  return aiFilledFields.value.size
}

const isAiFieldsStillVisible = (): boolean => {
  return getAiFieldCount() > 0
}

const clearAIFields = (): void => {
  for (const fieldName of aiFilledFields.value) {
    setFieldValue(fieldName, '')
  }
  aiFilledFields.value.clear()
}

const acceptAIFields = (): void => {
  aiFilledFields.value.clear()
}

const { value: OrganizationNameValue } = useField<string>('organization_name')
const { value: commercialRegisterValue } = useField('tw_commercial_register')
const { value: taxIdValue } = useField('tax_id')
const { value: vatNumberValue } = useField('tw_vat_number')
const { value: descriptionValue } = useField('tw_description')
const { value: industryTypeValue } = useField('tw_industry_type')

const onManualInput = (fieldName: string): void => {
  aiFilledFields.value.delete(fieldName)
}

const handleCommercialRegisterManualInput = (): void => {
  onManualInput('tw_commercial_register')
}

const handleTaxIdManualInput = (): void => {
  onManualInput('tax_id')
}

const handleVatNumberManualInput = (): void => {
  onManualInput('tw_vat_number')
}

const handleDescriptionManualInput = (): void => {
  onManualInput('tw_description')
}

const handleIndustryTypeManualInput = (): void => {
  onManualInput('tw_industry_type')
}

const handleAiClick = (): void => {
  emit('aiClick', OrganizationNameValue.value)
}

const handleAiCancel = (): void => {
  emit('aiCancel')
}

const fillFields = (): void => {
  const ai = props.aiData
  if (!ai) return

  if (!OrganizationNameValue.value) {
    setFieldValue('organization_name', ai.organization_name)
    aiFilledFields.value.add('organization_name')
  }

  if (!commercialRegisterValue.value && ai.commercial_register) {
    setFieldValue('tw_commercial_register', ai.commercial_register)
    aiFilledFields.value.add('tw_commercial_register')
  }

  if (!taxIdValue.value && ai.tax_id) {
    setFieldValue('tax_id', ai.tax_id)
    aiFilledFields.value.add('tax_id')
  }

  if (!vatNumberValue.value && ai.vat_id) {
    setFieldValue('tw_vat_number', ai.vat_id)
    aiFilledFields.value.add('tw_vat_number')
  }

  if (!descriptionValue.value && ai.description) {
    setFieldValue('tw_description', ai.description)
    aiFilledFields.value.add('tw_description')
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!industryTypeValue.value && ai.industry_type) {
    setFieldValue('tw_industry_type', ai.industry_type)
    aiFilledFields.value.add('tw_industry_type')
  }
}

onMounted(async () => {
  await fetchIndustryList()
  fillFields()
  if (isCustomer.value) {
    await fetchCustomerGroupList()
  } else if (isSupplier.value) {
    await fetchSupplierGroupList()
  }
})

watch(currentDoctype, async (doctype) => {
  if (doctype === RelationType.customers) {
    await fetchCustomerGroupList()
  } else if (doctype === RelationType.suppliers) {
    await fetchSupplierGroupList()
  }
})

watch(
  () => props.aiData,
  (newVal) => {
    if (newVal) {
      fillFields()
    }
  },
)

defineExpose({
  acceptAIFields,
  clearAIFields,
  getAiFieldCount,
  isAiFieldsStillVisible,
})
</script>

<!-- eslint-disable vue/v-on-handler-style -->
<template>
  <CollapsableContent
    :data-testid="TEST_IDS_CREATE.sectionGeneralDataOrganization.root"
    :title="tfDefault('editOrganization.generalDataTitle')"
  >
    <InputGrid>
      <TextInput
        ai-cancelable
        :ai-loading="aiLoading"
        :capitalize-first="false"
        :data-testid="TEST_IDS_CREATE.sectionGeneralDataOrganization.organizationName"
        :label="
          props.isPersonCustomer
            ? tfDefault('editOrganization.generalDataPersonName')
            : tfDefault('editOrganization.generalDataOrganizationName')
        "
        name="organization_name"
        required
        :show-ai-button="
          !!OrganizationNameValue &&
          ((inCreateContext ?? false) || (showAiButton ?? false)) &&
          !props.isPersonCustomer
        "
        @ai-cancel="handleAiCancel"
        @ai-click="handleAiClick"
        @blur="handleOrganizationNameBlur"
        @manual-input="handleOrganizationNameInput"
      />
      <TextInput
        v-if="!props.isPersonCustomer"
        key="tw_commercial_register"
        :data-testid="TEST_IDS_CREATE.sectionGeneralDataOrganization.registerNumber"
        :is-filled-by-ai="aiFilledFields.has('tw_commercial_register')"
        :label="tfDefault('editOrganization.generalDataRegisterNumber')"
        name="tw_commercial_register"
        @blur="onSubmit"
        @manual-input="handleCommercialRegisterManualInput"
      />
      <TextInput
        key="tax_id"
        :data-testid="TEST_IDS_CREATE.sectionGeneralDataOrganization.taxId"
        :is-filled-by-ai="aiFilledFields.has('tax_id')"
        :label="tfDefault('editOrganization.generalDataTaxId')"
        name="tax_id"
        @blur="onSubmit"
        @manual-input="handleTaxIdManualInput"
      />
      <TextInput
        v-if="!props.isPersonCustomer"
        key="tw_vat_number"
        :data-testid="TEST_IDS_CREATE.sectionGeneralDataOrganization.vatNumber"
        :is-filled-by-ai="aiFilledFields.has('tw_vat_number')"
        :label="tfDefault('editOrganization.generalDataVatNumber')"
        name="tw_vat_number"
        :placeholder="tfDefault('editOrganization.generalDataVatNumberPlaceholder')"
        @blur="onSubmit"
        @manual-input="handleVatNumberManualInput"
      />
    </InputGrid>
    <InputGrid>
      <TWTextArea
        :data-testid="TEST_IDS_CREATE.sectionGeneralDataOrganization.description"
        :is-filled-by-ai="aiFilledFields.has('tw_description')"
        :label="tfDefault('editOrganization.generalDataDescription')"
        name="tw_description"
        @blur="onSubmit"
        @manual-input="handleDescriptionManualInput"
      />
      <TWTextArea
        :data-testid="TEST_IDS_CREATE.sectionGeneralDataOrganization.metVia"
        :label="tfDefault('editOrganization.generalDataContactOrigin')"
        name="tw_contact_origin"
        :rows="1"
        @blur="onSubmit"
      />
    </InputGrid>

    <SingleSelect
      v-if="!props.isPersonCustomer"
      :data-testid="TEST_IDS_CREATE.sectionGeneralDataOrganization.industry"
      :is-filled-by-ai="aiFilledFields.has('tw_industry_type')"
      :label="tfDefault('editOrganization.generalDataIndustry')"
      name="tw_industry_type"
      on-white-bg
      :options="[industryOptionList]"
      searchable
      @blur="onSubmit"
      @manual-input="handleIndustryTypeManualInput"
    />
    <SingleSelect
      v-if="isCustomer && !props.isPersonCustomer"
      key="single-select-2"
      :label="tfDefault('editOrganization.generalDataCustomerGroup')"
      name="customer_group"
      on-white-bg
      :options="[customerGroupOptionList]"
      searchable
      @blur="onSubmit"
    />
    <SingleSelect
      v-if="isSupplier"
      key="single-select-3"
      :label="tfDefault('editOrganization.generalDataSupplierGroup')"
      name="supplier_group"
      on-white-bg
      :options="[supplierGroupOptionList]"
      searchable
      @blur="onSubmit"
    />
  </CollapsableContent>
</template>
